/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Menu, X, Home, LogOut, ChevronLeft, ChevronDown,
  Inbox, Users, BarChart3, AlertTriangle, CheckCircle2,
  ArrowLeft, MessageSquare, FileText, HandCoins, Banknote, Scale,
  Send, BadgeCheck, Calculator,
} from "lucide-react";
import { SuperRolesContants } from "../contants";
import { GetCustomerServiceMessagesUnread } from "../services/Actions/MessagesAction";
import { GetUserProfile, SignOut } from "../services/Actions/authActions";
import { GetTicketStats } from "../services/Actions/supportAction";
import ThemeToggle from "../Components/ThemeToggle";
import { RolesContants } from "../contants";

// Queue links carry a `badge` key read off the stats payload so the sidebar
// doubles as the agent's workload view, the way the loans shell surfaces
// unread message counts.
const NAV = [
  { label: "Dashboard", icon: <Home size={18} />, path: "/support/home" },
  {
    label: "Tickets",
    icon: <Inbox size={18} />,
    children: [
      { label: "All Tickets", icon: <Inbox size={15} />, path: "/support/tickets" },
      { label: "Escalated", icon: <AlertTriangle size={15} />, path: "/support/tickets?view=escalated", badge: "escalated" },
      { label: "Resolved", icon: <CheckCircle2 size={15} />, path: "/support/tickets?view=resolved" },
    ],
  },
  { label: "Messages", icon: <MessageSquare size={18} />, path: "/support/messages" },
  { label: "Members", icon: <Users size={18} />, path: "/support/members" },
  // Read-only lookups mirroring the loans module, so an agent can answer a
  // "where is my loan / was my fund deducted?" call without the loans role.
  { label: "Loan Status", icon: <FileText size={18} />, path: "/support/loan-status" },
  { label: "Loan Deduction", icon: <Banknote size={18} />, path: "/support/loan-deduction" },
  { label: "Funds Deduction", icon: <HandCoins size={18} />, path: "/support/funds-deduction" },
  // Quote a repayment on a call without leaving the desk.
  { label: "Loan Calculator", icon: <Calculator size={18} />, path: "/support/calculator" },
  {
    label: "Settlements",
    icon: <Scale size={18} />,
    children: [
      // Pending requests to quote and process.
      { label: "Requests", icon: <Scale size={15} />, path: "/support/settlements" },
      // Processed quotes sent to the member — opened from here to mark as paid.
      { label: "Sent", icon: <Send size={15} />, path: "/support/sent-settlements" },
      // Already settled.
      { label: "Paid", icon: <BadgeCheck size={15} />, path: "/support/paid-settlements" },
    ],
  },
  {
    label: "Reports",
    icon: <BarChart3 size={18} />,
    path: "/support/reports",
    roles: [RolesContants.CUSTOMER_SERVICE_HEAD],
  },
];

function RenderSupportPage({ children, title }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openSections, setOpenSections] = useState(["Tickets"]);

  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const auth = useSelector((state) => state.auth);
  const support = useSelector((state) => state.support);
  const messageState = useSelector((state) => state.message);
  const { user } = auth;
  const { stats } = support;
  const { supportMessagesUnread } = messageState;

  useEffect(() => {
    dispatch(GetUserProfile());
    dispatch(GetTicketStats());
    dispatch(GetCustomerServiceMessagesUnread());
  }, [dispatch]);

  // Head-only links are hidden rather than shown-and-bounced; the route guard
  // still enforces it (see PrivateCustomerServiceRoute).
  const isAdmin = user?.super_role === SuperRolesContants.ADMIN;
  const isHead =
    isAdmin ||
    (user?.role || []).includes(RolesContants.CUSTOMER_SERVICE_HEAD) ||
    user?.super_role === RolesContants.CUSTOMER_SERVICE_HEAD;

  const canSee = (item) => !item.roles || isHead;

  const badgeValue = (key) => {
    if (!key) return null;
    const n = stats?.[key];
    return n > 0 ? n : null;
  };

  const handleLogout = () => {
    dispatch(SignOut());
    setTimeout(() => window.location.reload(false), 500);
  };

  const toggleSection = (label) =>
    setOpenSections((prev) =>
      prev.includes(label) ? prev.filter((s) => s !== label) : [...prev, label]
    );

  // Query-string links (?view=mine) share a pathname, so match on the full
  // location rather than pathname alone.
  const currentPath = `${location.pathname}${location.search}`;
  const isLinkActive = (path) =>
    path.includes("?") ? currentPath === path : location.pathname === path;
  const isChildActive = (item) => item.children?.some((c) => isLinkActive(c.path));

  // Expand whichever section owns the current route, so landing on a child link
  // directly (or arriving from another page) doesn't leave it collapsed.
  useEffect(() => {
    const active = NAV.find((item) => isChildActive(item));
    if (active) {
      setOpenSections((prev) =>
        prev.includes(active.label) ? prev : [...prev, active.label]
      );
    }
  }, [location.pathname, location.search]);

  const go = (path) => {
    history.push(path);
    setIsMobileMenuOpen(false);
  };

  const sidebarWidth = isCollapsed ? "w-[68px]" : "w-64";
  const mainPadding = isCollapsed ? "lg:pl-[68px]" : "lg:pl-64";

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex py-1 transition-colors duration-300">
      {/* SIDEBAR */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 ${sidebarWidth} bg-white dark:bg-gray-900 border-r border-slate-200 dark:border-gray-700 transform transition-all duration-300 ease-in-out lg:translate-x-0 ${isMobileMenuOpen ? "translate-x-0 !w-64" : "-translate-x-full"}`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-3 flex items-center justify-between border-b border-slate-100 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                CS
              </div>
              {!isCollapsed && (
                <div>
                  <p className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-tight">
                    Customer Service
                  </p>
                  <p className="text-xs text-slate-400 dark:text-slate-500">
                    Member Support Desk
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="hidden lg:flex p-1.5 rounded-lg border border-slate-200 dark:border-gray-600 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
            >
              <ChevronLeft size={14} style={{ transform: isCollapsed ? "rotate(180deg)" : "none", transition: "transform 0.3s" }} />
            </button>
            <button onClick={() => setIsMobileMenuOpen(false)} className="lg:hidden p-2 text-slate-500">
              <X size={18} />
            </button>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-2 py-2 overflow-y-auto space-y-0.5 custom-scrollbar">
            {NAV.filter(canSee).map((item) => {
              if (!item.children) {
                const isActive = isLinkActive(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => go(item.path)}
                    title={isCollapsed ? item.label : ""}
                    className={`w-full flex items-center ${isCollapsed ? "justify-center" : "gap-3"} px-3 py-2.5 rounded-lg transition-all group ${isActive ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-800"}`}
                  >
                    <span className={isActive ? "text-white" : "text-slate-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400"}>
                      {item.icon}
                    </span>
                    {!isCollapsed && <span className="text-sm font-semibold">{item.label}</span>}
                  </button>
                );
              }

              const sectionOpen = openSections.includes(item.label);
              const hasActive = isChildActive(item);
              return (
                <div key={item.label}>
                  <button
                    onClick={() => !isCollapsed && toggleSection(item.label)}
                    title={isCollapsed ? item.label : ""}
                    className={`w-full flex items-center ${isCollapsed ? "justify-center" : "justify-between"} px-3 py-2.5 rounded-lg transition-all group ${hasActive ? "text-indigo-600 dark:text-indigo-400" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-800"}`}
                  >
                    <div className={`flex items-center ${isCollapsed ? "" : "gap-3"}`}>
                      <span className={hasActive ? "text-indigo-600 dark:text-indigo-400" : "text-slate-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400"}>
                        {item.icon}
                      </span>
                      {!isCollapsed && <span className="text-sm font-semibold">{item.label}</span>}
                    </div>
                    {!isCollapsed && (
                      <ChevronDown size={14} className={`transition-transform ${sectionOpen ? "rotate-180" : ""} text-slate-400`} />
                    )}
                  </button>

                  {!isCollapsed && sectionOpen && (
                    <div className="ml-4 pl-3 border-l-2 border-slate-100 dark:border-gray-700 space-y-0.5 mt-0.5 mb-1">
                      {item.children.map((child) => {
                        const isActive = isLinkActive(child.path);
                        const count = badgeValue(child.badge);
                        return (
                          <button
                            key={child.path}
                            onClick={() => go(child.path)}
                            className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-xs font-semibold transition-all ${isActive ? "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400" : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-gray-800 hover:text-slate-700 dark:hover:text-slate-200"}`}
                          >
                            <span className="flex items-center gap-2">
                              <span>{child.icon}</span>
                              {child.label}
                            </span>
                            {count !== null && (
                              <span className="px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300 text-[10px] font-bold">
                                {count}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-2 border-t border-slate-100 dark:border-gray-700">
            {/* An admin reaches this module from the admin sidebar, which
                navigates in-place — give them the way back. */}
            {isAdmin && (
              <button
                onClick={() => go("/admin/home")}
                title={isCollapsed ? "Back to Admin" : ""}
                className={`w-full flex items-center ${isCollapsed ? "justify-center" : "gap-2"} px-2 py-2 mb-2 rounded-xl border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors font-semibold text-sm`}
              >
                <ArrowLeft size={15} />
                {!isCollapsed && "Back to Admin"}
              </button>
            )}
            {!isCollapsed ? (
              <>
                <div className="flex items-center gap-2 mb-2 px-2">
                  <img
                    className="w-8 h-8 rounded-full border-2 border-white dark:border-gray-600 shadow-sm flex-shrink-0"
                    src={user?.profile_image || "/images/dev/avarta2.jpeg"}
                    alt="User"
                  />
                  <div className="overflow-hidden">
                    <p className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">
                      {user?.name || "Customer Service"}
                    </p>
                    <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                      {isHead ? "Service Head" : "Service Agent"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 px-2 py-2 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-600 text-rose-600 rounded-xl hover:bg-rose-50 dark:hover:bg-gray-700 transition-colors font-semibold text-sm shadow-sm"
                >
                  <LogOut size={15} /> Logout
                </button>
              </>
            ) : (
              <button onClick={handleLogout} title="Logout" className="w-full flex items-center justify-center py-2 text-rose-600 hover:bg-rose-50 dark:hover:bg-gray-700 rounded-xl transition-colors">
                <LogOut size={18} />
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* MAIN */}
      <main className={`flex-1 flex flex-col min-w-0 ${mainPadding} transition-all duration-300`}>
        <header className="sticky top-0 z-40 bg-white/60 dark:bg-gray-900/60 backdrop-blur-md border-b border-slate-200 dark:border-gray-700 px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => setIsMobileMenuOpen(true)} className="lg:hidden p-2 rounded-lg bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-slate-300">
                <Menu size={18} />
              </button>
              <h1 className="text-sm font-bold text-slate-800 dark:text-slate-100 hidden sm:block">{title}</h1>
            </div>
            <div className="flex items-center gap-3">
              {/* Member messages to Customer Service — same affordance the loans
                  and mart shells put in their headers. */}
              <button
                onClick={() => go("/support/messages")}
                title="Member messages"
                className="relative p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-gray-800 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              >
                <MessageSquare size={18} />
                {supportMessagesUnread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold">
                    {supportMessagesUnread > 99 ? "99+" : supportMessagesUnread}
                  </span>
                )}
              </button>

              <ThemeToggle />
              <img
                className="w-7 h-7 rounded-full cursor-pointer ring-2 ring-transparent hover:ring-indigo-500 transition-all shadow-sm"
                src={user?.profile_image || "/images/dev/avarta2.jpeg"}
                alt="Avatar"
              />
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-black text-slate-800 dark:text-slate-100 tracking-tight">{title}</h2>
            {title && <div className="h-1 w-12 bg-indigo-600 rounded-full mt-2" />}
          </div>
          <div className="animate-in fade-in duration-500">{children}</div>
        </div>
      </main>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 lg:hidden" onClick={() => setIsMobileMenuOpen(false)} />
      )}
    </div>
  );
}

export default RenderSupportPage;
